import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiAward, FiHeart, FiStar, FiUsers } from 'react-icons/fi';
import SEO from '../components/seo/SEO';
import { breadcrumb } from '../components/seo/jsonLd';

const VALUES = [
  { icon: FiHeart, title:'Passion', desc:'Every service is delivered with genuine care and artistic dedication.' },
  { icon: FiAward, title:'Excellence', desc:'We use only premium products and follow the highest industry standards.' },
  { icon: FiUsers, title:'Community', desc:'Building lasting relationships with our clients, one appointment at a time.' },
  { icon: FiStar, title:'Innovation', desc:'Staying ahead of trends so you always leave looking current and confident.' },
];

const STATS = [
  { value:'8+', label:'Years of Excellence' },
  { value:'5,000+', label:'Happy Clients' },
  { value:'12', label:'Expert Artists' },
  { value:'98%', label:'Satisfaction Rate' },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Salon Vora — Chennai's Luxury Beauty Studio"
        description="Founded in 2016, Salon Vora is more than a salon — it's a sanctuary. Meet the artists, values and story behind Chennai's premier luxury salon."
        path="/about"
        jsonLd={breadcrumb([
          { name: 'Home', url: '/' },
          { name: 'About', url: '/about' },
        ])}
      />
    <div className="min-h-screen bg-salon-cream">
      {/* Hero */}
      <section className="relative bg-hero-gradient py-32 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage:'radial-gradient(circle at 50% 50%, #8B1A4A 0%, transparent 70%)' }} />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-salon-primary/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-salon-secondary/15 blur-3xl pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.span initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
            className="section-tag mb-4 inline-block">Our Story</motion.span>
          <motion.h1 initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ delay:.1 }}
            className="font-display text-4xl md:text-6xl text-white mb-6">
            More Than a Salon —<br /><span className="text-salon-secondary">A Sanctuary</span>
          </motion.h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
          <motion.div initial={{ opacity:0, x:-30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }}>
            <h2 className="font-display text-3xl md:text-4xl text-salon-dark mb-6">Born from a Love of Beauty</h2>
            <p className="text-salon-muted leading-relaxed mb-5">
              Salon Vora was founded in 2016 with a singular vision: to create a space where every client feels seen, celebrated, and transformed. What began as a small boutique studio has grown into one of the city's most beloved luxury salons.
            </p>
            <p className="text-salon-muted leading-relaxed mb-5">
              Our founder, with over 15 years in the beauty industry, believed that exceptional hair and beauty services should come paired with a warm, welcoming experience — not just a transaction.
            </p>
            <p className="text-salon-muted leading-relaxed">
              Today, our team of 12 specialist artists continues that tradition, combining artisanal technique with the latest trends to deliver results that turn heads.
            </p>
          </motion.div>
          <motion.div initial={{ opacity:0, x:30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }}
            className="grid grid-cols-2 gap-4">
            {STATS.map(s => (
              <div key={s.label} className="card text-center py-8">
                <div className="font-display text-4xl text-salon-primary mb-2">{s.value}</div>
                <div className="text-salon-muted text-sm">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4 text-center">
          <span className="section-tag mb-4 inline-block">What Drives Us</span>
          <h2 className="font-display text-3xl md:text-4xl text-salon-dark mb-14">Our Core Values</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {VALUES.map((v, i) => (
              <motion.div key={v.title}
                initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} transition={{ delay:i*0.1 }} viewport={{ once:true }}
                className="card p-8 group hover:bg-salon-primary transition-colors duration-300">
                <v.icon className="w-10 h-10 text-salon-primary group-hover:text-white mx-auto mb-4 transition-colors" />
                <h3 className="font-display text-xl text-salon-dark group-hover:text-white mb-3 transition-colors">{v.title}</h3>
                <p className="text-salon-muted group-hover:text-white/80 text-sm transition-colors">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-salon-primary to-salon-dark text-center">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Experience the Vora Difference</h2>
          <p className="text-white/70 mb-8">Book your first appointment and discover why clients keep coming back.</p>
          <Link to="/book" className="btn-gold">Book an Appointment</Link>
        </div>
      </section>
    </div>
    </>
  );
}
